export enum GeoFenceAction {
  ENTER = "ENTER",
  EXIT = "EXIT",
}

export enum GeoFenceAccuracy {
  DESIRED_ACCURACY_NAVIGATION = "DESIRED_ACCURACY_NAVIGATION",
  DESIRED_ACCURACY_HIGH = "DESIRED_ACCURACY_HIGH",
  DESIRED_ACCURACY_MEDIUM = "DESIRED_ACCURACY_MEDIUM",
  DESIRED_ACCURACY_LOW = "DESIRED_ACCURACY_LOW",
}

export enum GeoFenceAddressType {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

export interface GeofenceListItem {
  _id: string;
  action: GeoFenceAction;
  timestamp: string;
  latitude: number;
  longitude: number;
  accuracy?: GeoFenceAccuracy;
  addressType?: GeoFenceAddressType;
  geofenceIdentifier?: string;
  error?: string;
  participantId: string;
  enrollmentId: string;
  testResultId?: string;
  notificationId?: string;
  participantName: string;
  programName: string;
  source?: string;
  deviceId?: string;
  osType?: string;
  locationAccuracy?: number;
  batteryLevel?: number;
  createdAt: string;

  // COYN-1347: backend-derived phantom flag — true when the EXIT event sat
  // clearly inside the configured radius and was not promoted to a
  // VALIDATION_REQUEST + push. Undefined on records persisted before the
  // gate was deployed.
  isPhantom?: boolean;

  // COYN-1347: server-computed troubleshooting fields (same Haversine as
  // the detail endpoint). The frontend uses them to infer a verdict for
  // rows where isPhantom is undefined — i.e. events created before the
  // phantom-EXIT gate was deployed. Each is independently undefined when
  // the underlying input is missing (no address coordinates or no program
  // radius).
  distanceToCenterMeters?: number;
  geofenceRadiusMeters?: number;
  isInsideRadius?: boolean;
}

export interface GeofenceSearchResponse {
  geofences: GeofenceListItem[];
  totalCount: number;
}

export interface GeofenceSearchParams {
  pageNumber: number;
  pageSize: number;
  sortField: string;
  sortOrder: number;
  search?: string;
  action?: string;
  addressType?: string;
  programId?: string;
  // COYN-1347: optional participant filter used by the trajectory + heatmap
  // overlays on the detail map. The backend already accepts `participantId`
  // on the search endpoint; we just hadn't surfaced it through the frontend
  // service / hook because the list page didn't need it.
  participantId?: string;
  dateFrom?: string;
  dateTo?: string;
  isPhantom?: "true" | "false" | "unclassified";
}

export interface ValidationRequestSettings {
  metersRadiusGeofenceRequestValidation?: number;
  metersGeofenceAccuracyThreshold?: number;
  minutesPingGeofenceValidationRequest?: number;
  minutesPingGeofenceAlive?: number;
  automaticGeofenceTrigger?: boolean;
  requestGeofenceValidationsPerDay?: number;
  minimumMinsGeofenceBetweenRequestValidations?: number;
  showGeofenceChip?: boolean;
  distanceFilterGeofenceRequestValidation?: number;
  timerValidationRequestOpened?: number;
  minutesTimeToCompleteValidation?: number;
  validationRequestMaxAttempts?: number;
}

export interface GeofenceDetailParticipantAddress {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  zipCode?: string;
  county?: string;
  latitude?: number;
  longitude?: number;
  secondaryAddressLine1?: string;
  secondaryAddressLine2?: string;
  secondaryCity?: string;
  secondaryState?: string;
  secondaryPostalCode?: string;
  secondaryZipCode?: string;
  secondaryCounty?: string;
  secondaryLatitude?: number;
  secondaryLongitude?: number;
}

export interface GeofenceDetailParticipant {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  countryPhoneCode?: string;
  status?: string;
  address?: GeofenceDetailParticipantAddress;
}

export interface GeofenceDetailProgram {
  _id: string;
  name: string;
  configuration?: {
    validationRequestSettings?: ValidationRequestSettings;
  };
}

export interface GeofenceDetailEnrollment {
  _id: string;
  status: string;
  programId: string;
  startDate?: string;
  endDate?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt?: string;
}

// Expanded TR view so the detail page can render the full validation lifecycle.
export interface GeofenceDetailTestResult {
  _id: string;
  status: string;
  validationType: string;
  isOnDemand?: boolean;
  validationNumber?: number;
  createdAt: string;
  updatedAt?: string;
  notificationSentAt?: string;
  validationStartedAt?: string;
  validationActionedAt?: string;
  validationExpiredAt?: string;
  completedAt?: string;
  carriageConfirmationResponse?: string;
  carriageConfirmationOpenedAt?: string;
  carriageConfirmationRespondedAt?: string;
  missedValidationMetadata?: Record<string, unknown>;
  nonCarriageMetadata?: Record<string, unknown>;
  enrollmentId?: string;
}

export interface GeofenceDetailNotification {
  _id: string;
  channel: string;
  status?: string;
  type: string;
  sentAt?: string;
  createdAt?: string;
  enrollmentId?: string;
  testResultId?: string;
}

export interface GeofenceDetail {
  _id: string;
  action: GeoFenceAction;
  timestamp: string;
  latitude: number;
  longitude: number;
  error?: string;
  accuracy?: GeoFenceAccuracy;
  geofenceIdentifier?: string;
  addressType?: GeoFenceAddressType;
  osType?: string;
  source?: string;
  deviceId?: string;
  uuid?: string;
  locationAccuracy?: number;
  speed?: number;
  batteryLevel?: number;
  batteryCharging?: boolean;
  createdAt: string;

  // COYN-1347: backend-derived phantom flag.
  isPhantom?: boolean;

  // COYN-1347: server-computed troubleshooting fields. The backend measures
  // the great-circle distance between the event coordinates and the relevant
  // geofence centre (primary or secondary based on `addressType`), reads the
  // configured radius from program config, and precomputes the inside flag.
  // All three are `undefined` when the data needed to compute them is
  // missing (no participant address, no program radius, etc.).
  distanceToCenterMeters?: number;
  geofenceRadiusMeters?: number;
  isInsideRadius?: boolean;

  participant?: GeofenceDetailParticipant;
  enrollment?: GeofenceDetailEnrollment;
  program?: GeofenceDetailProgram;
  testResult?: GeofenceDetailTestResult;
  notification?: GeofenceDetailNotification;
}
